<template>
  <page-container
    ref="pageContainerRef"
    class="hotelux-home-page"
    :class="{
      'header-sticky': isHeaderSticky || isHeaderLeaving,
      'header-leaving': isHeaderLeaving
    }"
    :show-navbar="false"
    :show-header="false"
  >
    <hotelux-header
      class="header"
      :logo-url="logoUrl"
      :show-currency="true"
      :is-sticky="isHeaderSticky"
    >
      <template #center>
        <transition name="sticky-search">
          <search-bar
            v-if="isHeaderSticky"
            class="header-search"
            :is-sticky="true"
            @search-click="handleSearch"
            @search-change="handleSearchChange"
          />
        </transition>
      </template>
    </hotelux-header>

    <main class="hotelux-home">
      <section class="hero">
        <el-carousel
          v-if="heroBanners.length"
          ref="heroCarouselRef"
          class="hero-carousel"
          :autoplay="heroBanners.length > 1"
          :interval="4000"
          :arrow="heroBanners.length > 1 ? 'hover' : 'never'"
          indicator-position="none"
          @change="handleHeroChange"
        >
          <el-carousel-item
            v-for="banner in heroBanners"
            :key="banner._id || banner.img"
          >
            <button
              type="button"
              class="hero-banner"
              :class="{
                clickable: isClickable(banner),
                'hero-banner--jp': language === 'jp'
              }"
              :style="{
                backgroundImage: `url(${encodeURI(heroImage(banner))})`
              }"
              @click="handleEntryClick(banner)"
            >
              <img
                v-if="banner.extendedProp?.partnerLogo"
                class="hero-partner-logo"
                :src="banner.extendedProp.partnerLogo"
                alt=""
              />
              <UIText type="title" v-if="heroTitle(banner)" class="hero-title">
                {{ heroTitle(banner) }}
              </UIText>
              <UIText v-if="heroSubtitle(banner)" class="hero-subtitle">
                {{ heroSubtitle(banner) }}
              </UIText>
              <UIText v-if="heroAction(banner)" class="hero-action">
                {{ heroAction(banner) }}
              </UIText>
            </button>
          </el-carousel-item>
        </el-carousel>
        <div v-if="heroBanners.length > 1" class="hero-indicators">
          <button
            v-for="(banner, index) in heroBanners"
            :key="index"
            type="button"
            class="indicator-dot"
            :class="{ active: index === heroActiveIndex }"
            :aria-label="heroTitle(banner)"
            :aria-current="index === heroActiveIndex"
            @click="handleHeroIndicatorClick(index)"
          />
        </div>
        <search-bar
          v-if="!isHeaderSticky"
          ref="heroSearchRef"
          class="hero-search"
          @search-click="handleSearch"
          @search-change="handleSearchChange"
        />
      </section>
      <section
        class="home-section membership-section"
        v-if="memberLevelIsGuest"
      >
        <div class="membership-header">
          <UIText type="bold" class="membership-label">{{
            t("shared.auth.membershipMatch")
          }}</UIText>
          <UIText class="title" italic type="title">{{
            t("shared.auth.upgradeMembership")
          }}</UIText>
          <UIText class="membership-desc">
            {{ t("shared.auth.upgradeMembershipTips") }}
          </UIText>
        </div>
        <div class="membership-cards">
          <div
            class="membership-card mr-6"
            @click="toPath('/credit-card-match')"
          >
            <div class="membership-card-media">
              <img
                src="https://qiniu-uploads.charmdeer.com/Airline_Loyalty_Match.png"
                alt=""
              />
            </div>
            <div class="pr-3 pl-3">
              <UIText
                type="title"
                class="byCard"
                :class="{ 'byCard--en': language === 'en' }"
                >{{ t("shell.home.upgradeMembership.byCard") }}</UIText
              >
              <UIText class="subtitle">
                {{ t("shell.home.upgradeMembership.byCardTips") }}
              </UIText>
              <UIText type="bold" class="match">{{
                t("shared.auth.creditCard.matchNow")
              }}</UIText>
            </div>
          </div>
          <div class="membership-card" @click="toPath('/airline-match')">
            <div class="membership-card-media">
              <img
                src="https://qiniu-uploads.charmdeer.com/CreditCard.png"
                alt=""
              />
            </div>
            <div class="pr-3 pl-3">
              <UIText
                type="title"
                class="byCard"
                :class="{ 'byCard--en': language === 'en' }"
                >{{ t("shell.home.upgradeMembership.byAirline") }}</UIText
              >
              <UIText class="subtitle">{{
                t("shell.home.upgradeMembership.byAirlineTips")
              }}</UIText>
              <UIText type="bold" class="match">{{
                t("shared.auth.creditCard.matchNow")
              }}</UIText>
            </div>
          </div>
          <!-- <button
            v-for="card in homeStore.membershipMatch"
            :key="card._id"
            type="button"
            class="membership-card"
            @click="handleEntryClick(card)"
          >
            <h3>{{ getI18nText(card.titleI18n) }}</h3>
            <p>{{ getI18nText(card.subtitleI18n) }}</p>
            <span class="membership-action">
              {{ getI18nText(card.extendedProp?.actionLabelI18n) }}
            </span>
          </button> -->
        </div>
      </section>
      <div class="bg-[#F5F5F5]">
        <section
          v-if="homeStore.exclusiveOffers.length"
          class="home-section exclusive-section"
          :class="{ 'is-member': !memberLevelIsGuest }"
        >
          <UIText
            type="title"
            class="header-title"
            :class="{ 'header-title--en': language === 'en' }"
            >{{ getI18nText(homeStore.sectionTitles.exclusiveOffers) }}</UIText
          >
          <div class="offer-list">
            <button
              v-for="offer in homeStore.exclusiveOffers"
              :key="offer._id"
              type="button"
              class="offer-card"
              @click="handleEntryClick(offer)"
            >
              <span class="offer-image">
                <img
                  :src="$optimizeQiniuImage(desktopImage(offer), 2)"
                  :alt="getI18nText(offer.titleI18n)"
                />
              </span>
              <UIText
                type="bold"
                v-if="getI18nText(offer.badgeI18n)"
                class="offer-badge"
              >
                {{ getI18nText(offer.badgeI18n) }}
              </UIText>
              <span class="offer-content">
                <UIText type="title" class="title">{{
                  getI18nText(offer.titleI18n)
                }}</UIText>
                <UIText
                  class="desc"
                  v-if="getI18nText(offer.extendedProp?.timeI18n)"
                >
                  {{ getI18nText(offer.extendedProp?.timeI18n) }}
                </UIText>
                <UIText class="desc" v-if="offer.extendedProp?.price">
                  {{ offer.extendedProp.price }}
                </UIText>
              </span>
            </button>
          </div>
        </section>

        <section class="home-section curated-section" v-if="hotels.length > 0">
          <UIText
            type="title"
            class="header-title"
            :class="{ 'header-title--en': language === 'en' }"
            >{{
              getI18nText(homeStore.sectionTitles.curatedCollections)
            }}</UIText
          >
          <div v-loading="hotelsLoading" class="hotel-list">
            <curated-hotel-card
              v-for="hotel in hotels"
              :key="hotel._id"
              :hotel="hotel"
              @select="handleSelectHotel(hotel)"
            />
          </div>
        </section>
      </div>
      <section v-if="homeStore.destinations.length" class="destination-links">
        <div class="destination-box">
          <div
            v-for="destination in homeStore.destinations"
            :key="getI18nText(destination.nameI18n)"
            class="destination-group"
          >
            <UIText type="bold">{{ getI18nText(destination.nameI18n) }}</UIText>
            <UIText
              type="light"
              v-for="hotel in destination.hotels"
              :key="hotel._id"
              class="button"
              @click="navigateToHotel(hotel)"
            >
              {{ getI18nText(hotel.nameI18n) }}
            </UIText>
          </div>
        </div>
      </section>
    </main>
  </page-container>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import bus from "vue3-eventbus";

import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useHoteluxHomeStoreHook } from "@/distributions/consumer/y/store/hoteluxHome";
import { recordLocationHistory } from "@/distributions/consumer/y/utils/historyHelper";
import { syncHotelDetailLocation } from "@/distributions/consumer/y/utils/hotelNavigation";
import { useHotels } from "@/hooks/useHotels";
import { getI18nText } from "@/plugins/i18n";
import { useUserStoreHook } from "@/store/modules/user";
import { paramsToQuery } from "@/utils/tool";

import { useYChannelStoreHook } from "../../../../store/channel";
import HoteluxHeader from "../../components/Header/index.vue";
import CuratedHotelCard from "../../components/index/CuratedHotelCard.vue";
import SearchBar from "../../components/SearchBar/index.vue";
import UIText from "../../components/UiText/index.vue";

const router = useRouter();
const { t } = useI18n();
const channelStore = useYChannelStoreHook();
const hotelStore = useHotelStoreHook();
const homeStore = useHoteluxHomeStoreHook();
const userStore = useUserStoreHook();
const { searchModel } = storeToRefs(hotelStore);
const { language, preferred, user } = storeToRefs(userStore);
const DEFAULT_HERO = {
  img: "https://uploads.ytripos.com/uploaded/lkTce8_RRZXTg2mvkzB-I1uPdazV/y-web-index-bg2.png"
};

const logoUrl = computed(
  () => channelStore.configs?.pages?.Index?.logoUrl || ""
);
const memberLevelIsGuest = computed(() => {
  return (
    !user.value ||
    (user.value?.loyaltyProgram?.ref?.type &&
      user.value?.loyaltyProgram?.ref?.type === "guest")
  );
});
const heroBanners = computed(() => {
  if (!homeStore.isLoaded) return [];
  console.log(homeStore.banners, "homeStore.banners");
  const entryBanners = homeStore.banners.filter(
    item =>
      (item.imageUrl ||
        getI18nText(item.imageUrlI18n) ||
        item.imageAlternative?.imageUrl) &&
      // 后台运营位（有 _id）的标题只认当前语言，当前语言没配标题就整条过滤掉
      (!item._id || Boolean(getI18nText(item.titleI18n, { fallback: false })))
  );
  return entryBanners.length ? entryBanners : [DEFAULT_HERO];
});
const curatedFilter = computed(() => ({
  hotels: homeStore.curatedHotelIds.map(_id => ({ _id }))
}));

const heroCarouselRef = ref(null);
const heroActiveIndex = ref(0);

const handleHeroChange = index => {
  heroActiveIndex.value = index;
};

const handleHeroIndicatorClick = index => {
  if (index === heroActiveIndex.value) return;
  heroCarouselRef.value?.setActiveItem(index);
};

const {
  hotels,
  loading: hotelsLoading,
  fetchHotels
} = useHotels({
  pageSize: 6,
  stay: searchModel.value.stay,
  filter: curatedFilter,
  preferred
});

const desktopImage = item =>
  item?.imageAlternative?.imageUrl ||
  getI18nText(item?.imageUrlI18n) ||
  item?.imageUrl ||
  "";
const heroImage = item => {
  return (
    item?.imageAlternative?.imageUrl ||
    getI18nText(item?.imageUrlI18n) ||
    item?.imageUrl ||
    item?.img ||
    ""
  );
};

const heroTitle = item =>
  item?._id ? getI18nText(item.titleI18n) : item?.title ? t(item.title) : "";
const heroSubtitle = item =>
  item?._id
    ? getI18nText(item.subtitleI18n)
    : item?.subtitle
    ? t(item.subtitle)
    : item?.content
    ? t(item.content)
    : "";
const heroAction = item =>
  item?._id
    ? getI18nText(item.extendedProp?.actionLabelI18n)
    : item?.btn
    ? t(item.btn)
    : "";
const entryLink = item =>
  item?.linkAlternative?.path ? item.linkAlternative : item?.link;
const isClickable = item =>
  Boolean(item?.onClick || entryLink(item)?.path || item?.target?.ref);

const handleEntryClick = item => {
  if (item?.onClick) {
    item.onClick({ router, userStore });
    return;
  }

  const target = item?.target?.ref;
  const targetRef = target?._id || target;
  if (targetRef) {
    navigateToHotel(typeof target === "object" ? target : { _id: targetRef });
    return;
  }

  const link = entryLink(item);
  if (!link?.path) return;
  if (["webpage", "browser", "externalApp"].includes(link.type)) {
    // 桌面端配置「当前窗口」时用站内抽屉的 iframe 承载，其余情况新开窗口
    if (link.extendedProp?.target === "_self") {
      bus.emit("openJcbBenefitDrawer", link.path);
      return;
    }
    window.open(link.path, "_blank", "noopener,noreferrer");
    return;
  }
  router.push(link.path);
};

const ROUTER_PATH_MAP = {
  moduleTpHotel: "/hotel/detail",
  city: "/hotel/list",
  googleMapPolitical: "/hotel/list",
  search: "/hotel/list"
};
const handleSearchChange = () => {
  const query = paramsToQuery();
  if (query?.locationType === "moduleTpHotel") handleSearch();
};
const handleSearch = () => {
  recordLocationHistory(hotelStore.hotelSearchModel.location);
  const query = paramsToQuery();
  router.push({
    path: ROUTER_PATH_MAP[query.locationType] || "/hotel/list",
    query
  });
};

const toPath = path => {
  router.push({
    path
  });
};

const navigateToHotel = hotel => {
  hotelStore.SET_SELECTED_HOTEL(hotel);
  const locationQuery = syncHotelDetailLocation(
    hotelStore,
    hotel,
    language.value
  );
  router.push({
    path: "/hotel/detail",
    query: {
      ...locationQuery,
      checkIn: hotelStore.searchModel.stay.date.checkIn,
      checkOut: hotelStore.searchModel.stay.date.checkOut,
      numberOfAdults: hotelStore.searchModel.stay.guest.numberOfAdults,
      numberOfChildren: hotelStore.searchModel.stay.guest.numberOfChildren,
      numberOfRooms: hotelStore.searchModel.stay.guest.numberOfRooms,
      hotelId: hotel._id
    }
  });
};
const handleSelectHotel = hotel => navigateToHotel(hotel);

const isHeaderSticky = ref(false);
const pageContainerRef = ref(null);
const heroSearchRef = ref(null);

// 取消吸顶时页头要滑出，动画播完再恢复成原始页头
const isHeaderLeaving = ref(false);
let headerLeaveTimer = null;

// 需与样式里的动画时长保持一致
const HEADER_TRANSITION = 260;

watch(isHeaderSticky, value => {
  clearTimeout(headerLeaveTimer);
  isHeaderLeaving.value = !value;
  if (!value) {
    headerLeaveTimer = setTimeout(() => {
      isHeaderLeaving.value = false;
    }, HEADER_TRANSITION);
  }
});

// 搜索条完全滑出可视区时的 scrollTop，滑动到该位置后才吸顶
let stickyTriggerTop = null;

const handleScroll = event => {
  const target = event?.target || pageContainerRef.value?.$refs?.pageRef;
  if (!target) return;

  const searchEl = heroSearchRef.value?.$el;
  const searchRect = searchEl?.getBoundingClientRect?.();
  const hostRect = target.getBoundingClientRect?.();
  const scrollTop = target.scrollTop || 0;

  if (searchRect && hostRect) {
    stickyTriggerTop = scrollTop + searchRect.bottom - hostRect.top;
  }

  if (stickyTriggerTop === null) return;
  isHeaderSticky.value = scrollTop >= stickyTriggerTop;
};

onMounted(async () => {
  await homeStore.fetchHome();
  await fetchHotels();
  await nextTick();

  setTimeout(() => {
    const pageElement = pageContainerRef.value?.$refs?.pageRef;
    if (pageElement) {
      pageElement.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll({ target: pageElement });
    }
  }, 100);
});

onUnmounted(() => {
  clearTimeout(headerLeaveTimer);

  const pageElement = pageContainerRef.value?.$refs?.pageRef;
  if (pageElement) {
    pageElement.removeEventListener("scroll", handleScroll);
  }
});

defineOptions({ name: "HoteluxHome" });
</script>

<style lang="scss" scoped>
.hotelux-home-page {
  // 仅本页面：禁用浏览器横向滑动前进/后退手势，避免与卡片横向滚动冲突
  overscroll-behavior-x: none;

  :deep(.wrapper) {
    min-width: 1200px;
    min-height: calc(100vh + 500px);
    padding-top: 0 !important;
  }

  :deep(.header) {
    --y-currency-selector-color: #fff;

    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    width: 100%;
    height: 104px;
    padding: 28px max(60px, calc((100% - 1200px) / 2));
    color: #fff;
    background: transparent;
    transition: all 0.3s ease;
  }

  :deep(.header .y-current-user) {
    --y-text-color: #fff;
  }

  &.header-sticky {
    :deep(.header) {
      position: fixed !important;
      height: auto !important;
      padding: 16px max(60px, calc((100% - 1200px) / 2)) !important;
      color: #212223 !important;
      background: #f8f8f8 !important;
      box-shadow: 0 2px 8px rgb(0 0 0 / 8%) !important;
      animation: hotelux-header-enter 260ms ease both;

      --y-currency-selector-color: #212223;
    }

    :deep(.header .y-current-user) {
      --y-text-color: #212223;
    }
  }

  // 取消吸顶：页头保持吸顶定位向顶部滑出，动画结束后才回到原始页头
  &.header-leaving {
    :deep(.header) {
      animation: hotelux-header-leave 260ms ease both;
    }
  }
}

@keyframes hotelux-header-enter {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
}

@keyframes hotelux-header-leave {
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
}

// 吸顶页头里的搜索条随页头一起淡入，避免硬切
.sticky-search-enter-active {
  transition: opacity 240ms ease, transform 240ms ease;
}

.sticky-search-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.header-search {
  flex: 1;
  padding: 0 24px;

  :deep(.hotelux-search-bar) {
    padding: 0;
  }
}

.hotelux-home {
  position: relative;
  width: 100%;
  min-height: 100vh;
  color: #212223;
  background: #fff;
  font-family: Lato, -apple-system, sans-serif;
}

.hero {
  position: relative;
  width: 100%;
  height: 600px;
}

.hero-carousel,
:deep(.hero-carousel .el-carousel__container) {
  height: 100%;
}

.hero-banner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 140px 60px 120px;
  color: #fff;
  background-position: center;
  background-size: cover;
  border: 0;

  &::after {
    position: absolute;
    inset: 0;
    content: "";
    background: rgb(0 0 0 / 40%);
  }

  &.clickable {
    cursor: pointer;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}
.byCard {
  font-size: 22px;
  color: #212223;
  margin-bottom: 12px;
  font-size: 22px;
  color: #212223;
  letter-spacing: 0;
  display: inline-block;
}
.byCard--en {
  font-family: "Cormorant-Bold" !important;
}
.hero-title {
  max-width: 1200px;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  letter-spacing: 0;
}

.hero-partner-logo {
  max-width: 180px;
  max-height: 72px;
  margin-bottom: 24px;
  object-fit: contain;
}

.hero-subtitle {
  max-width: 1000px;
  margin-top: 16px;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.5;
  color: #d8d8d8;
  text-align: center;
}

.hero-action {
  min-width: 140px;
  padding: 11px 20px;
  margin-top: 32px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #fff;
  letter-spacing: 0.5px;
  text-align: center;
  background: transparent;
  border: 1px solid #fff;
  transition: all 0.3s ease;

  &:hover {
    background: rgb(255 255 255 / 10%);
  }
}

// 日文横幅：标题用 Noto Serif JP，副标题与按钮用 Noto Sans JP（均为正常粗细）
.hero-banner--jp {
  .hero-title {
    font-family: NotoSerifJP-Variable, serif;
  }

  .hero-subtitle,
  .hero-action {
    font-family: NotoSansJP-Variable, sans-serif;
  }
}

.hero-indicators {
  position: absolute;
  bottom: 100px;
  left: 50%;
  z-index: 2;
  display: flex;
  gap: 9px;
  align-items: center;
  transform: translateX(-50%);
}

.indicator-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  cursor: pointer;
  background: #bfbfbf;
  border: 0;
  border-radius: 50%;
  transition: all 0.3s ease;

  &.active {
    width: 10px;
    height: 10px;
    background: #fff;
  }
}

.hero-search {
  --search-bar-inner-width: min(1224px, calc(100vw - 120px));

  position: absolute;
  right: 0;
  bottom: -45px;
  left: 0;
  z-index: 3;

  :deep(.search-bar-inner) {
    min-height: 56px;
    padding: 16px 24px;
    background: #fff;
    border: 0;
    box-shadow: 0 2px 8px rgb(0 0 0 / 8%);
  }
}

.home-section {
  width: min(1200px, calc(100% - 120px));
  // min-width: 1200px;
  padding: 80px 0 60px;
  margin: 0 auto;

  .header-title {
    margin-bottom: 48px;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.2;
    color: #212223;
    text-align: left;
    letter-spacing: 1px;
  }
  .header-title--en {
    font-family: Cormorant-BoldItalic !important;
  }
}

.membership-section {
  padding: 145px 0 100px;
  background: #fff;
  display: flex;
  justify-content: space-between;
  // margin-top: 80px;
}

.membership-header {
  margin-bottom: 56px;
  text-align: left;
  width: 488px;
  max-width: 488px;
  margin-right: 72px;

  .membership-label {
    display: inline-block;
    margin-bottom: 16px;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    // font-family: "lato-Bold";
    color: #757575;
    letter-spacing: 2px;
  }

  .title {
    display: block;
    margin-bottom: 20px;
    font-size: 48px;
  }

  .membership-desc {
    font-size: 16px;
    line-height: 24px;
    // font-family: "lato";
    color: #757575;
  }
}

.membership-cards {
  display: flex;
  // grid-template-columns: repeat(2, minmax(0, 1fr));
  // gap: 32px;
}

.membership-card {
  width: 308px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  cursor: pointer;
  background: #fff;

  // 图片悬停放大后由这层裁掉溢出，保证卡片布局不被撑开
  .membership-card-media {
    width: 100%;
    height: 385px;
    margin-bottom: 16px;
    overflow: hidden;
  }

  img {
    width: 100%;
    height: 100%;
    // 提前提升为合成层，让放大交给 GPU 合成，避免逐帧重绘造成卡顿
    will-change: transform;
    transition: transform 400ms ease-in-out;
  }

  &:hover img {
    transform: scale(1.06);
  }

  .subtitle {
    flex: 1;
    margin-bottom: 32px;
    font-size: 16px;
    line-height: 1.25;
    color: #757575;
    // font-family: "lato";
  }

  .membership-action {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.125;
    color: #212223;
    letter-spacing: 1px;
  }
  .match {
    // font-family: "lato-bold";
    font-size: 16px;
    line-height: 18px;
    text-decoration-line: underline;
    text-transform: uppercase;
    color: #212223;
    margin-bottom: 0;
    letter-spacing: 1px;
    &:hover {
      color: #c7754d;
    }
  }
}

// 优惠卡片列表向右铺满到屏幕边缘，只在卡片两端保留与其他区块一致的留白
.home-section.exclusive-section {
  width: 100%;
  padding: 100px 0 0;

  // 会员态下上方没有会员升级区块，需要更大的上内边距
  &.is-member {
    padding-top: 145px;
  }
  .header-title,
  h2,
  .offer-list {
    padding-right: max(60px, calc((100% - 1200px) / 2));
    padding-left: max(60px, calc((100% - 1200px) / 2));
  }
}

.offer-list {
  display: flex;
  gap: 24px;
  overflow-x: auto;
  align-items: flex-start;
}

.offer-card {
  position: relative;
  flex: 0 0 384px;
  padding: 0;
  color: #212223;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;

  // 图片悬停放大后由这层裁掉溢出，阴影也随之落在这层，避免被裁掉
  .offer-image {
    display: block;
    height: 240px;
    overflow: hidden;
    box-shadow: 0 14px 30px rgb(0 0 0 / 10%);
    transition: box-shadow 0.3s ease;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    // 提前提升为合成层，让放大交给 GPU 合成，避免逐帧重绘造成卡顿
    will-change: transform;
    transition: transform 400ms ease-in-out;
  }

  &:hover {
    .offer-image {
      box-shadow: 0 18px 36px rgb(0 0 0 / 16%);
    }

    img {
      transform: scale(1.06);
    }
  }
}

.offer-badge {
  position: absolute;
  top: 19px;
  left: 19px;
  z-index: 1;
  max-width: calc(100% - 38px);
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  color: #212223;
  letter-spacing: 1px;
  text-transform: uppercase;
  overflow-wrap: break-word;
  background: rgb(255 255 255 / 90%);
  border: 1px solid #fff;
  // font-family: "lato-Bold";
}

.offer-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 12px 0;

  .title {
    // font-family: Cormorant, var(--y-title-font), serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 27px;
    letter-spacing: 0;
  }

  .desc {
    font-size: 16px;
    line-height: 20px;
    color: #757575;
    // font-family: "lato";
    letter-spacing: 0;
  }
}

// 精选酒店列表同样向右铺满到屏幕边缘，只在卡片两端保留与其他区块一致的留白
.home-section.curated-section {
  width: 100%;
  padding: 100px 0;
  // background: #f5f5f5;

  .header-title,
  h2,
  .hotel-list {
    padding-right: max(60px, calc((100% - 1200px) / 2));
    padding-left: max(60px, calc((100% - 1200px) / 2));
  }
}

.hotel-list {
  display: flex;
  gap: 24px;
  min-height: 385px;
  overflow-x: auto;

  :deep(.curated-hotel-card) {
    flex: 0 0 384px;
    width: 384px;
  }
}

.offer-list,
.hotel-list {
  // padding-bottom: 16px;
  overscroll-behavior-x: contain;
  // 卡片铺满到屏幕边缘后不再展示横向滚动条，仅保留横向滚动能力
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.destination-links {
  width: 100%;
  padding: 80px max(60px, calc((100% - 1200px) / 2)) 0;
  color: rgb(255 255 255 / 90%);
  background: #212223;
}
.destination-box {
  width: 100%;
  display: grid;
  gap: 48px 40px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  border-bottom: 0.5px solid rgba(241, 242, 243, 0.5);
  padding-bottom: 50px;
}

.destination-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  h3 {
    margin-bottom: 12px;
    font-family: Lato, -apple-system, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.125;
    color: #fff;
    letter-spacing: 2px;
  }

  .button {
    padding: 0;
    font-size: 12px;
    line-height: 2;
    color: inherit;
    text-align: left;
    cursor: pointer;
    font-family: "lato-light";
    background: transparent;
    border: 0;
    transition: color 0.2s ease;

    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: #c7754d;
    }
  }
}
</style>
